import React, { useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useQueryParams, useUserCompatibility } from '@/hooks';
import { useOnce } from '@/hooks/useOnce';
import { ProfilePageQueryParamsEnum } from '@/pages/profile/ProfilePageQueryParams.enum';
import { useStores } from '@/stores';
import { AppDownloadLink } from '@/types';
import { BigBlueButtonStateEnum } from '@/types/bigBlueButtonState.enum';
import { ProfileOptionsContext } from '../../../profile-options.context';
import { ProfileContext } from '../../../profile.context';
import {
  AvailableOS,
  DownloadButton,
  NoDownloadLinksAvailable,
  OsIcon,
  PlatformOptionInPrimaryButton,
} from './appDownloadButton.sc';
import {
  getAvailableOS,
  getDownloadItemByCurrentOS,
  OSConfig,
  resolve,
  SupportedOS,
} from './appDownloadService';
import { SelectPlatformWithPopover } from './selectPlatformWithPopover';

export const AppDownloadButton = observer(() => {
  const profile = useContext(ProfileContext);
  const { onDownload } = useContext(ProfileOptionsContext);
  const [items, setItems] = useState<AppDownloadLink[]>([]);
  const queryParams = useQueryParams();
  const { isUserIncompatibleWithCurrentGallery } = useUserCompatibility();
  const { userStore, compatibilitiesStore } = useStores();

  useEffect(() => {
    const resolveItems = resolve(profile).map((item) => ({
      ...item,
      buttonState: BigBlueButtonStateEnum.PRIMARY,
    }));
    setItems(resolveItems);
  }, [profile]);

  const userBrandId = useMemo(() => {
    if (!userStore.user) {
      return;
    }
    return compatibilitiesStore.getCompatibilityByIdOrSubBrandId(
      userStore.user.brandId,
    )?.id;
  }, [userStore.awaiting, userStore.user?.brandId]);

  const downloadOnlyOnce = useOnce(onDownload);

  useEffect(() => {
    if (userStore.awaiting) {
      return;
    }
    const appDownload = queryParams[ProfilePageQueryParamsEnum.appDownload];
    const canUserDownload = !userBrandId || profile.brand === userBrandId;
    if (
      appDownload?.toLowerCase() === 'true' &&
      items.length > 0 &&
      canUserDownload
    ) {
      const target = queryParams[ProfilePageQueryParamsEnum.TARGET];
      const downloadItem = getDownloadItemByCurrentOS(items, target);
      if (downloadItem) {
        downloadOnlyOnce(downloadItem);
      }
    }
  }, [queryParams, items, userStore.awaiting, userBrandId, profile.brand]);

  const availableOS = useMemo<AppDownloadLink[]>(
    () => getAvailableOS(profile.downloadLinks || []),
    [profile.downloadLinks],
  );

  return (
    <React.Fragment>
      {items.length === 0 && (
        <NoDownloadLinksAvailable
          data-test-automation-id={`no-download-links-available`}
        >
          {resolveCurrentlyOnlyAvailableFor(availableOS)}
        </NoDownloadLinksAvailable>
      )}
      {items.length === 1 && (
        <DownloadButton
          onClick={() => {
            onDownload!(items[0]);
          }}
          disabled={isUserIncompatibleWithCurrentGallery}
        >
          <PlatformOptionInPrimaryButton
            item={items[0]}
            useIconForActive
            useIconForDisabled={isUserIncompatibleWithCurrentGallery}
          />
        </DownloadButton>
      )}
      {items.length > 1 && <SelectPlatformWithPopover items={items} />}
    </React.Fragment>
  );
});

const resolveCurrentlyOnlyAvailableFor = (
  availableOS: AppDownloadLink[],
): any => {
  if (availableOS.length === 1 && availableOS[0].os === SupportedOS.OtherOS) {
    return 'Currently only available for other OS';
  }

  return (
    <React.Fragment>
      Currently only available for:
      <AvailableOS>
        {availableOS.map((item) => (
          <OsIcon
            key={item.os}
            symbol={OSConfig[item.os].icon}
            size="medium"
            className={`${item.os}`}
            data-test-automation-id={`${item.os}-os-icon`}
          />
        ))}
      </AvailableOS>
    </React.Fragment>
  );
};
